import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import {
    GetConfigValuesGQL,
    SetConfigValuesGQL,
} from '../../../../../generated/graphql.generated';
import { FreyaNotificationsService } from '../../../../services/freya-notifications.service';
import { dayjs } from '../../../../core/dayjs/dayjs';
import { DispatchActions } from '../../store/dispatch.actions';
import { selectAutoGeneratedBlocks, selectPendingChanges } from '../../store/dispatch.selectors';
import {
    buildDefaultEntryFromEvent,
    DefaultBlockConfig,
    eventMatchesDefaultEntry,
    findMatchingDefaultEntry,
    isBlockType,
    parseDefaultBlocksConfig as parseDefaultBlocks,
} from '../../store/dispatch.default-blocks';

interface CustomBlockData {
    type: string;
    dispatchDate: Date;
    defaultStart: number;
    defaultEnd: number;
    existingName?: string;
    existingEventId?: string;
    existingCrewRequirement?: { crewLeads: number; crewMembers: number; opsPeople: number };
}

@Component({
    standalone: true,
    selector: 'app-custom-block-dialog',
    imports: [CommonModule, FormsModule, ButtonModule, InputTextModule, InputNumberModule, CalendarModule, DropdownModule, TooltipModule],
    template: `
        <div class="custom-block-dialog">
            <div class="p-fluid">
                <div class="p-field">
                    <label for="blockName">Block Name</label>
                    <input
                        pInputText
                        id="blockName"
                        type="text"
                        [(ngModel)]="name"
                        [placeholder]="namePlaceholder"
                    />
                </div>
                @if (data.type === 'reserve' || data.type === 'admin') {
                    <div class="p-field crew-req-row">
                        <label>Crew Requirement</label>
                        <div class="crew-req-inputs">
                            <div class="crew-req-input">
                                <span>Crew Leads</span>
                                <p-inputNumber
                                    [(ngModel)]="crewLeadsRequired"
                                    [min]="0"
                                    [max]="10"
                                    [showButtons]="true"
                                    buttonLayout="horizontal"
                                    inputStyleClass="crew-req-num"
                                />
                            </div>
                            <div class="crew-req-input">
                                <span>Crew Members</span>
                                <p-inputNumber
                                    [(ngModel)]="crewMembersRequired"
                                    [min]="0"
                                    [max]="20"
                                    [showButtons]="true"
                                    buttonLayout="horizontal"
                                    inputStyleClass="crew-req-num"
                                />
                            </div>
                        </div>
                    </div>
                }
                @if (data.type === 'ops-manager') {
                    <div class="p-field crew-req-row">
                        <label>Ops People Required</label>
                        <p-inputNumber
                            [(ngModel)]="opsPeopleRequired"
                            [min]="1"
                            [max]="10"
                            [showButtons]="true"
                            buttonLayout="horizontal"
                            inputStyleClass="crew-req-num"
                        />
                    </div>
                }
                <div class="p-field">
                    <label>Time Block</label>
                    <div class="time-row">
                        <p-calendar
                            [(ngModel)]="startTime"
                            [timeOnly]="true"
                            hourFormat="12"
                            [stepMinute]="15"
                            [readonlyInput]="true"
                            appendTo="body"
                            [inputStyle]="{ width: '110px' }"
                            (onSelect)="onTimeChanged()"
                        ></p-calendar>
                        <span class="time-sep">to</span>
                        <p-calendar
                            [(ngModel)]="endTime"
                            [timeOnly]="true"
                            hourFormat="12"
                            [stepMinute]="15"
                            [readonlyInput]="true"
                            appendTo="body"
                            [inputStyle]="{ width: '110px' }"
                            (onSelect)="onTimeChanged()"
                        ></p-calendar>
                    </div>
                </div>
            </div>
            <div class="p-d-flex p-jc-between p-mt-3">
                <div class="p-d-flex">
                    <p-button
                        *ngIf="isDefaultBlock && !isSavingDefaultBlock"
                        label="Remove as default"
                        icon="pi pi-bookmark-fill"
                        severity="danger"
                        [outlined]="true"
                        (onClick)="onRemoveAsDefaultBlock()"
                        pTooltip="Stop auto-creating this block for every day"
                        tooltipPosition="bottom"
                        styleClass="p-mr-2"
                    ></p-button>
                    <p-button
                        *ngIf="!isDefaultBlock"
                        label="Add as default"
                        icon="pi pi-bookmark"
                        [outlined]="true"
                        [loading]="isSavingDefaultBlock"
                        (onClick)="onAddAsDefaultBlock()"
                        pTooltip="Auto-create this block for every day"
                        tooltipPosition="bottom"
                    ></p-button>
                    <p-button
                        *ngIf="data.existingEventId"
                        label="Delete"
                        icon="pi pi-trash"
                        severity="danger"
                        [outlined]="true"
                        (onClick)="onDelete()"
                        styleClass="p-ml-2"
                    ></p-button>
                </div>
                <div class="p-d-flex">
                    <p-button
                        label="Cancel"
                        [outlined]="true"
                        (onClick)="ref.close()"
                        styleClass="p-mr-2"
                    ></p-button>
                    <p-button
                        label="OK"
                        (onClick)="onCreate()"
                    ></p-button>
                </div>
            </div>
        </div>
    `,
    styles: [`
        .custom-block-dialog {
            padding: 1rem;
        }
        .p-field {
            margin-bottom: 0.75rem;
        }
        label {
            display: block;
            margin-bottom: 0.25rem;
            font-size: 0.85rem;
        }
        .crew-req-row {
            .crew-req-inputs {
                display: flex;
                gap: 1rem;
            }
            .crew-req-input {
                flex: 1;
                span {
                    display: block;
                    font-size: 0.75rem;
                    margin-bottom: 0.25rem;
                    color: #888;
                }
            }
            .crew-req-num {
                width: 100%;
            }
        }
        .time-sep {
            margin: 0 0.5rem;
            color: var(--text-color-secondary, #6b7280);
        }
    `],
})
export class CustomBlockDialogComponent implements OnInit {
    public name: string = '';
    public namePlaceholder: string = 'Enter a name';
    public crewLeadsRequired = 0;
    public crewMembersRequired = 0;
    public opsPeopleRequired = 1;

    public startTime: Date = new Date();
    public endTime: Date = new Date();

    /** Whether this block already matches an entry in the zone's default-blocks config. */
    public isDefaultBlock = false;
    public isSavingDefaultBlock = false;

    public data: CustomBlockData;

    constructor(
        public ref: DynamicDialogRef,
        private config: DynamicDialogConfig,
        private getConfigValuesGQL: GetConfigValuesGQL,
        private setConfigValuesGQL: SetConfigValuesGQL,
        private notify: FreyaNotificationsService,
        private store: Store,
    ) {
        this.data = (config?.data as CustomBlockData) || {
            type: 'reserve',
            dispatchDate: new Date(),
            defaultStart: 0,
            defaultEnd: 0,
        };

        this.startTime = new Date((this.data.defaultStart || dayjs(this.data.dispatchDate).startOf('day').add(7, 'hours').unix()) * 1000);
        this.endTime = new Date((this.data.defaultEnd || dayjs(this.data.dispatchDate).startOf('day').add(17, 'hours').unix()) * 1000);

        // Use existing name if editing, otherwise default by type
        if (this.data.existingName) {
            this.name = this.data.existingName;
            this.namePlaceholder = this.data.existingName;
        } else if (this.data.type === 'ops-manager') {
            this.name = 'Ops Manager';
            this.namePlaceholder = 'Ops Manager';
        } else if (this.data.type === 'reserve') {
            this.name = 'Reserve';
            this.namePlaceholder = 'Reserve';
        } else if (this.data.type === 'admin') {
            this.name = 'Admin';
            this.namePlaceholder = 'Admin';
        }

        // Load existing crew requirements if editing
        if (this.data.existingCrewRequirement) {
            this.crewLeadsRequired = this.data.existingCrewRequirement.crewLeads || 0;
            this.crewMembersRequired = this.data.existingCrewRequirement.crewMembers || 0;
            this.opsPeopleRequired = this.data.existingCrewRequirement.opsPeople || 1;
        }
    }

    ngOnInit(): void {
        // Only block types can be saved as defaults, and only when editing an
        // existing event (a new block has no time shape worth persisting yet).
        if (this.data.existingEventId && isBlockType(this.data.type)) {
            this.refreshIsDefaultBlock();
        }
    }

    /** Whether this block already matches an entry in the zone's default-blocks config. */
    private refreshIsDefaultBlock() {
        this.fetchDefaultBlocks().subscribe({
            next: (entries) => {
                this.isDefaultBlock = !!findMatchingDefaultEntry(this.dialogEvent(), entries);
            },
            error: () => {
                this.isDefaultBlock = false;
            },
        });
    }

    /** The event shape used for default-block matching (type + title + times). */
    private dialogEvent() {
        return {
            type: this.data.type,
            title: this.name,
            start: Math.floor(this.startTime.getTime() / 1000),
            end: Math.floor(this.endTime.getTime() / 1000),
            metadata: {},
        };
    }

    /** Fetch + parse the zone's calendarEvents.defaultBlocks config. */
    private fetchDefaultBlocks() {
        return new Observable<DefaultBlockConfig[]>((subscriber) => {
            this.getConfigValuesGQL.fetch({ keys: ['calendarEvents.defaultBlocks'] }, { fetchPolicy: 'no-cache' })
                .subscribe({
                    next: (res) => {
                        const configValue = res?.data?.getConfigValues?.[0];
                        const raw = configValue?.value ?? configValue?.fallback?.value ?? null;
                        subscriber.next(parseDefaultBlocks(raw));
                        subscriber.complete();
                    },
                    error: (err) => subscriber.error(err),
                });
        });
    }

    /** Persist a new entries array to the zone config. `onSaved` runs only
     *  after the save succeeds (e.g. the Remove-as-Default shell deletion
     *  must not fire on a failed save — CodeRabbit: the board's ensure
     *  effect would immediately re-create them). */
    private saveDefaultBlocks(entries: DefaultBlockConfig[], added: boolean, onSaved?: () => void) {
        this.isSavingDefaultBlock = true;
        this.setConfigValuesGQL.mutate({ configs: [{ key: 'calendarEvents.defaultBlocks', value: JSON.stringify(entries) }] })
            .subscribe({
                next: () => {
                    this.isSavingDefaultBlock = false;
                    this.isDefaultBlock = added;
                    onSaved?.();


                    // Re-sync the dispatch board's in-memory config — it only
                    // reloads on zone load, so without this the board kept
                    // acting on the STALE entries: removed defaults were
                    // re-created on every subsequently opened day, added ones
                    // never applied (Ray: "Set/Remove as Default does not
                    // consistently apply across all days", issue #6983). The
                    // reducer also clears the per-day ensured bookkeeping on
                    // this action so already-visited days backfill on
                    // navigation. The backend cascade (config.valuesSet
                    // subscriber in calendarEvents.service) clears the shells
                    // already stamped on other days.
                    this.store.dispatch(DispatchActions.defaultBlocksConfigLoaded({ entries }));

                    this.notify.success(added
                        ? 'Added to default blocks'
                        : 'Removed from default blocks');
                },
                error: () => {
                    this.isSavingDefaultBlock = false;
                    this.notify.error('Failed to update default blocks config');
                },
            });
    }

    /** "Add as default" — append this block's shape to the zone config. */
    public onAddAsDefaultBlock() {
        const event = this.dialogEvent();
        const entry = buildDefaultEntryFromEvent(event);
        if (!entry) {
            return this.notify.error('This block cannot be saved as a default');
        }

        this.fetchDefaultBlocks().subscribe({
            next: (entries) => {
                // Drop any same-shape entry first (type + title) so we never duplicate
                const filtered = entries.filter((e) => !eventMatchesDefaultEntry(event, e));
                this.saveDefaultBlocks([...filtered, entry], true);
            },
            error: () => this.notify.error('Failed to load default blocks config'),
        });
    }

    /** "Remove as default" — drop the matching entry from the zone config. */
    public onRemoveAsDefaultBlock() {
        const event = this.dialogEvent();
        this.fetchDefaultBlocks().subscribe({
            next: (entries) => {
                const remaining = entries.filter((e) => !eventMatchesDefaultEntry(event, e));
                if (remaining.length === entries.length) {
                    this.isDefaultBlock = false;
                    return;
                }

                // Deleting the config entry only stops future creation — the
                // shells already stamped onto the board stay (Ray: "Remove as
                // Default does not fully remove the Ops Manager on all days").
                // The matching auto-generated shells on the loaded day are
                // deleted AFTER the config save succeeds (dispatching the
                // deletion before a failed save would both queue the board's
                // shells for removal and leave them re-created by the ensure
                // effect, out of sync with the still-live config); the
                // backend cascade on the config save clears every other day.
                const deleteShells = () => {
                    if (!event.type || !event.title) { return; }
                    this.store.select(selectAutoGeneratedBlocks).pipe(take(1)).subscribe((blocks) => {
                        for (const block of blocks) {
                            if (block.type === event.type && block.title === event.title) {
                                this.store.dispatch(DispatchActions.blockDeleted({ eventId: block.id }));
                            }
                        }
                    });
                    // The board retains queued creates for OTHER dates
                    // (the persisted pendingChanges). A create for a removed
                    // default would otherwise still submit after the config
                    // save and re-create the block (CodeRabbit). Manual
                    // (user-drawn) creates never match the config shape and
                    // stay.
                    this.store.select(selectPendingChanges).pipe(take(1)).subscribe((changes) => {
                        for (const change of changes) {
                            if (change.type === 'createEvent'
                                && change.eventData?.eventType === event.type
                                && change.eventName === event.title) {
                                this.store.dispatch(DispatchActions.pendingChangeRemoved({ changeId: change.id }));
                            }
                        }
                    });
                };

                this.saveDefaultBlocks(remaining, false, deleteShells);
            },
            error: () => this.notify.error('Failed to load default blocks config'),
        });
    }

    public onTimeChanged(): void {
        if (this.endTime.getTime() <= this.startTime.getTime()) {
            this.endTime = new Date(this.startTime.getTime() + 15 * 60 * 1000);
        }
    }

    public onCreate(): void {
        this.ref.close({
            name: this.name,
            start: Math.floor(this.startTime.getTime() / 1000),
            end: Math.floor(this.endTime.getTime() / 1000),
            crewRequirement: this.data.type === 'ops-manager'
                ? { crewLeads: 0, crewMembers: 0, opsPeople: this.opsPeopleRequired }
                : { crewLeads: this.crewLeadsRequired, crewMembers: this.crewMembersRequired, opsPeople: 0 },
        });
    }

    public onDelete(): void {
        this.ref.close({ delete: true, eventId: this.data.existingEventId });
    }
}